import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';

const cores = {
  navy: '#1A2233', border: '#d4dde8', muted: '#6b7a8d', orange: '#D85A30',
  green: '#27ae60', red: '#e74c3c',
};
const inputStyle = { padding: '9px 12px', border: `1.5px solid ${cores.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, fontWeight: 700, color: cores.muted, display: 'block', marginBottom: 4 };
const btnPrimary = { padding: '10px 20px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' };
const btnGhost = { padding: '10px 20px', background: '#f0f3f7', color: cores.muted, border: `1px solid ${cores.border}`, borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' };

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailValido = (n) => n.email_valido !== undefined ? n.email_valido : EMAIL_REGEX.test((n.email || '').trim());

// Pausa entre cada email — envio SEMPRE sequencial (nunca em paralelo), pra
// não estourar rate limit do provedor (Resend/SMTP) e não tomar bloqueio
// enviando muitos de uma vez. Ajustável se precisar mais/menos cautela.
const INTERVALO_ENTRE_EMAILS_MS = 1500;

// Modal de envio MANUAL de email das notas avulsas/importadas em massa.
// Propositalmente separado da emissão — o envio nunca é automático (ver
// discussão: 2000 notas de uma vez tomariam bloqueio do provedor de email).
// O usuário escolhe quantas/quais notas mandar em cada lote e acompanha o
// progresso item a item.
export default function EnviarEmailsNotasModal({ onClose, toast }) {
  const [carregando, setCarregando] = useState(true);
  const [apenasPendentes, setApenasPendentes] = useState(true);
  const [notas, setNotas] = useState([]);
  const [selecionadas, setSelecionadas] = useState(new Set());
  const [quantidadeRapida, setQuantidadeRapida] = useState(20);
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState({}); // { [notaId]: 'enviando' | 'sucesso' | 'erro' }
  const [erroDetalhe, setErroDetalhe] = useState({}); // { [notaId]: mensagem }

  const carregar = async () => {
    setCarregando(true);
    try {
      const { data } = await api.get('/notas/emails-pendentes', { params: { apenas_pendentes: apenasPendentes } });
      setNotas(data || []);
      setSelecionadas(new Set());
    } catch (e) {
      toast('Erro ao carregar notas.', 'error');
    }
    setCarregando(false);
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [apenasPendentes]);

  const toggleSelecionada = (id) => {
    setSelecionadas(prev => {
      const novo = new Set(prev);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });
  };

  const selecionarTodas = () => setSelecionadas(new Set(notas.filter(emailValido).map(n => n.id)));
  const limparSelecao = () => setSelecionadas(new Set());

  // Seleciona as N primeiras (topo da lista) que ainda não estão selecionadas — pula email inválido
  const selecionarProximasN = () => {
    const n = parseInt(quantidadeRapida) || 0;
    if (n <= 0) return;
    const proximas = notas.filter(x => emailValido(x) && !selecionadas.has(x.id)).slice(0, n).map(x => x.id);
    setSelecionadas(prev => new Set([...prev, ...proximas]));
  };

  const totalValorSelecionado = useMemo(
    () => notas.filter(n => selecionadas.has(n.id)).reduce((s, n) => s + (Number(n.valor) || 0), 0),
    [notas, selecionadas]
  );

  const enviarSelecionadas = async () => {
    const ids = [...selecionadas];
    if (ids.length === 0) { toast('Selecione ao menos uma nota.', 'error'); return; }
    setEnviando(true);
    setProgresso(Object.fromEntries(ids.map(id => [id, 'enviando'])));
    setErroDetalhe({});

    let sucesso = 0, erro = 0;
    // Sequencial de propósito — nunca Promise.all aqui.
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      try {
        await api.post('/email-orcamento/enviar-nota-avulsa', { nota_fiscal_id: id });
        setProgresso(prev => ({ ...prev, [id]: 'sucesso' }));
        sucesso++;
      } catch (e) {
        setProgresso(prev => ({ ...prev, [id]: 'erro' }));
        setErroDetalhe(prev => ({ ...prev, [id]: e?.response?.data?.erro || 'Erro ao enviar' }));
        erro++;
      }
      if (i < ids.length - 1) await new Promise(r => setTimeout(r, INTERVALO_ENTRE_EMAILS_MS));
    }

    setEnviando(false);
    toast(`${sucesso} email(s) enviado(s)${erro > 0 ? `, ${erro} com erro` : ''}.`, erro > 0 ? 'error' : 'success');
    // Recarrega a lista (as enviadas com sucesso saem se "apenas pendentes" estiver ligado)
    carregar();
  };

  return (
    <div onClick={e => e.target === e.currentTarget && !enviando && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 820, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>

        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${cores.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: cores.navy }}>✉️ Enviar Notas por Email</div>
          {!enviando && <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: cores.muted }}>✕</button>}
        </div>

        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${cores.border}`, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: cores.navy, fontWeight: 600 }}>
            <input type="checkbox" checked={apenasPendentes} onChange={e => setApenasPendentes(e.target.checked)} disabled={enviando} />
            Mostrar só quem ainda não recebeu email
          </label>

          {notas.some(n => !emailValido(n)) && (
            <div style={{ fontSize: 12, color: cores.red, fontWeight: 600 }}>
              ⚠️ {notas.filter(n => !emailValido(n)).length} com email inválido — serão ignorados automaticamente
            </div>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>SELECIONAR OS PRÓXIMOS</label>
              <input
                type="number" min={1} value={quantidadeRapida}
                onChange={e => setQuantidadeRapida(e.target.value)}
                style={{ ...inputStyle, width: 80 }}
                disabled={enviando}
              />
            </div>
            <button onClick={selecionarProximasN} style={btnGhost} disabled={enviando}>Selecionar</button>
            <button onClick={selecionarTodas} style={btnGhost} disabled={enviando}>Todas ({notas.length})</button>
            <button onClick={limparSelecao} style={btnGhost} disabled={enviando}>Limpar</button>
          </div>
        </div>

        <div style={{ padding: '0 24px', overflowY: 'auto', flex: 1 }}>
          {carregando ? (
            <div style={{ padding: 40, textAlign: 'center', color: cores.muted }}>Carregando...</div>
          ) : notas.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: cores.muted }}>
              {apenasPendentes ? 'Nenhuma nota pendente de envio de email.' : 'Nenhuma nota encontrada.'}
            </div>
          ) : (
            <div style={{ border: `1px solid ${cores.border}`, borderRadius: 10, overflow: 'hidden', marginTop: 12, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '28px 1.6fr 1.8fr 1fr 1fr 1fr', gap: 8, padding: '8px 12px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', position: 'sticky', top: 0 }}>
                <span></span><span>Cliente</span><span>Email</span><span>Valor</span><span>Nº Nota</span><span>Status</span>
              </div>
              {notas.map(n => {
                const st = progresso[n.id];
                const invalido = !emailValido(n);
                return (
                  <div key={n.id} style={{ display: 'grid', gridTemplateColumns: '28px 1.6fr 1.8fr 1fr 1fr 1fr', gap: 8, padding: '8px 12px', borderTop: `1px solid ${cores.border}`, fontSize: 13, alignItems: 'center', opacity: invalido ? 0.55 : 1 }}>
                    <input type="checkbox" checked={selecionadas.has(n.id)} onChange={() => toggleSelecionada(n.id)} disabled={enviando || invalido} title={invalido ? 'Email em formato inválido — não pode ser enviado' : ''} />
                    <span>{n.cliente_nome || '—'}</span>
                    <span style={{ color: cores.muted, fontSize: 12 }}>{n.email}</span>
                    <span>{Number(n.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                    <span>{n.numero_nota || '—'}</span>
                    <span style={{ fontWeight: 700, color: invalido ? cores.red : st === 'sucesso' ? cores.green : st === 'erro' ? cores.red : st === 'enviando' ? cores.orange : (n.email_enviado_em ? cores.green : cores.muted) }}>
                      {invalido ? '⚠️ Email inválido'
                        : st === 'enviando' ? '⏳ Enviando...'
                        : st === 'sucesso' ? '✅ Enviado'
                        : st === 'erro' ? `❌ ${erroDetalhe[n.id] || 'Erro'}`
                        : n.email_enviado_em ? '✅ Já enviado' : 'Pendente'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: `1px solid ${cores.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 12, color: cores.muted }}>
            {selecionadas.size > 0 && (
              <>{selecionadas.size} selecionada(s) — total {totalValorSelecionado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={btnGhost} disabled={enviando}>{enviando ? 'Aguarde...' : 'Fechar'}</button>
            <button onClick={enviarSelecionadas} style={btnPrimary} disabled={enviando || selecionadas.size === 0}>
              {enviando ? 'Enviando...' : `Enviar ${selecionadas.size} email(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
